import * as Sentry from "@sentry/react-router";

interface HydrationErrorInfo {
	componentStack?: string;
	digest?: string;
}

/**
 * Sampling rate for hydration error reporting.
 * Most hydration errors are caused by browser extensions or are otherwise benign.
 * We only report a small sample to Sentry for monitoring purposes.
 * This follows how large-scale sites (Google, Amazon) handle hydration mismatches.
 */
const HYDRATION_ERROR_SAMPLE_RATE = 0.01; // 1% of errors

/**
 * Patterns that indicate benign hydration mismatches that should never be reported.
 * These are typically caused by:
 * - Browser extensions injecting content
 * - Date/time formatting differences between server and client timezones
 * - Price formatting with locale differences
 * - User-specific dynamic content
 */
const BENIGN_HYDRATION_PATTERNS = [
	// Browser extension injections
	/extension/i,
	/grammarly/i,
	/lastpass/i,
	/1password/i,
	/bitwarden/i,
	/dashlane/i,
	/honey/i,
	/rakuten/i,

	// Date/time mismatches (server vs client timezone)
	/<time/i,
	/datetime/i,
	/\d{1,2}:\d{2}/,
	/\d{1,2}\.\d{2}\.\d{4}/,
	/\d{4}-\d{2}-\d{2}/,

	// Price formatting differences
	/€\s*\d/,
	/\d+[,\.]\d{2}\s*€/,
	/currency/i,

	// Dynamic user content
	/avatar/i,
	/username/i,
	/user-menu/i,
];

/**
 * Determines if a hydration error is benign and should not be reported.
 */
function isBenignHydrationError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;

	const message = error.message;
	return BENIGN_HYDRATION_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Extracts the specific element that caused the hydration mismatch from the error message.
 */
function extractFailedElement(error: unknown): string | undefined {
	if (!(error instanceof Error)) return undefined;

	const patterns = [
		/Expected server HTML to contain a matching (<[^>]+>)/,
		/Did not expect server HTML to contain a (<[^>]+>)/,
		/Text content does not match/,
		/Hydration failed because/,
	];

	for (const pattern of patterns) {
		const match = error.message.match(pattern);
		if (match) {
			return match[1] || error.message.slice(0, 100);
		}
	}

	return undefined;
}

/**
 * Reports hydration error to Sentry with context and logs to console for debugging.
 *
 * This function implements a sampling strategy to avoid overwhelming Sentry with
 * hydration errors, which are often caused by browser extensions or benign
 * server/client rendering differences. Only a small percentage of errors are
 * reported, and known-benign patterns are filtered out entirely.
 */
export function reportHydrationError(
	error: unknown,
	errorInfo: HydrationErrorInfo,
): void {
	// Skip benign hydration errors entirely
	if (isBenignHydrationError(error)) {
		return;
	}

	// Sample hydration errors - only report a small percentage
	// This prevents Sentry from being overwhelmed while still providing visibility
	if (Math.random() > HYDRATION_ERROR_SAMPLE_RATE) {
		return;
	}

	const failedElement = extractFailedElement(error);
	const errorMessage = error instanceof Error ? error.message : String(error);

	// Report to Sentry with level "warning" instead of "error"
	// since hydration mismatches are recoverable and often benign
	Sentry.captureException(
		error instanceof Error ? error : new Error(errorMessage),
		{
			level: "warning",
			tags: {
				errorType: "hydration",
				failedElement: failedElement?.slice(0, 50),
				sampled: "true",
			},
			extra: {
				digest: errorInfo.digest,
				sampleRate: HYDRATION_ERROR_SAMPLE_RATE,
			},
			fingerprint: ["hydration-error", failedElement || "unknown"],
		},
	);
}
